import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useCreateTemplate from './queries/useCreateTemplate';
import useGetTemplate from './queries/useGetTemplate';
import useUpdateTemplate from './queries/useUpdateTemplate';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrail,
    breadCrumbTrailEdit,
    addHeader,
    editHeader,
    initialState,
    templatesPath,
} from './constants';
import { templateSchema } from './schema';
import TemplateIdField from './formFields/TemplateIdField';
import TemplateNameField from './formFields/TemplateNameField';
import TemplateImageField from './formFields/TemplateImageField';

const TemplateForm = ({ mode = 'add' }) => {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);

    const {
        data: templateData,
        isLoading: templateLoading,
        isFetching: templateFetching,
        isError: notFound,
    } = useGetTemplate(id, isEdit);

    const handleSuccess = () => {
        toast.success(
            isEdit
                ? 'Template updated successfully!'
                : 'Template created successfully!',
        );
        navigate(templatesPath);
    };

    const handleError = (err) => {
        console.error(
            `Failed to ${isEdit ? 'update' : 'create'} template:`,
            err,
        );
        setError(
            err?.response?.data?.message ||
                `Failed to ${isEdit ? 'update' : 'create'} template. Please try again.`,
        );
    };

    const { mutate: createTemplate, isPending: createPending } =
        useCreateTemplate(handleSuccess, handleError);

    const { mutate: updateTemplate, isPending: updatePending } =
        useUpdateTemplate(handleSuccess, handleError);

    const isPending = isEdit ? updatePending : createPending;

    const finalFormSubmit = async (values, { setErrors, setTouched }) => {
        setError(null);

        let validated;
        try {
            validated = await templateSchema.validate(values, {
                abortEarly: false,
            });
        } catch (validationError) {
            const fieldErrors = {};
            (validationError.inner || []).forEach((innerError) => {
                if (innerError.path && !fieldErrors[innerError.path]) {
                    fieldErrors[innerError.path] = innerError.message;
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                setTouched(
                    Object.keys(fieldErrors).reduce(
                        (touchedFields, field) => ({
                            ...touchedFields,
                            [field]: true,
                        }),
                        {},
                    ),
                );
            } else {
                setError(validationError.message);
            }
            return;
        }

        if (isEdit) {
            updateTemplate({
                id: validated.id,
                name: validated.name,
                newImage: values.newImage,
            });
        } else {
            createTemplate({
                id: validated.id,
                name: validated.name,
                newImage: values.newImage,
            });
        }
    };

    const formFields = (errors, touched, _values, setFieldValue) =>
        [
            <TemplateIdField
                key="id"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
                isEditMode={isEdit}
            />,
            <TemplateNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <TemplateImageField
                key="newImage"
                darkMode={darkMode}
                existingImage={templateData?.image}
                setFieldValue={setFieldValue}
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    if (isEdit && (templateLoading || templateFetching)) {
        return <LoadingIndicator />;
    }

    if (isEdit && notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrailEdit}
                header={editHeader}
                initialValues={initialState}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[templatesPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This template no longer exists. It may have already been
                        deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const formInitialValues = isEdit
        ? {
              id: templateData?.id ?? '',
              name: templateData?.name || '',
              newImage: null,
          }
        : initialState;

    return (
        <FormBase
            isSubmitDisabled={isPending}
            isLoading={isPending}
            breadCrumbTrail={isEdit ? breadCrumbTrailEdit : breadCrumbTrail}
            header={isEdit ? editHeader : addHeader}
            initialValues={formInitialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={[templatesPath, { state: { index: 0 } }]}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default TemplateForm;
